import React, { useContext, useEffect, useState } from "react";
import { Container, Row, Col } from "reactstrap";
import { useParams } from "react-router-dom";
import SectionCard from "../../../../Components/Common/SectionCard";
import FullPageLoader from "../../../../Components/Common/FullPageLoader";
import FilePreviewModal from "../../../../Components/Common/FilePreviewModal";
import DetailField from "../../../../Components/Common/DetailField";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import addVolunteerStyle from "../../MasterVolunteer/css/volunteer.module.scss";
import styles from "../css/addBlockVolunteer.module.scss";
import { useBlockVolunteerService } from "../api/useBlockVolunteerQuery";
import { useVolunteerService } from "modules/Volunteer/MasterVolunteer/api/useVolunteerQuery";
import { formatDate } from "common/service/CommonService";
import { BreadcrumbContext } from "Layouts/Header";
import { BLOCK_VOLUNTEER_PATHS } from "Routes/paths";
import TruncatedText from "Components/Common/TruncatedText";

const ViewBlockVolunteer: React.FC = () => {
  document.title = "View Block Record | ZENTIXS VISTA";

  const { id } = useParams<{ id: string }>();

  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const breadcrumbContext = useContext(BreadcrumbContext);

  const { SelectQuery } = useBlockVolunteerService({
    blockid: id ? Number(id) : null,
  });

  const data = SelectQuery?.data?.data?.[0];

  const { SelectQuery: VolunteerSelectQuery } = useVolunteerService({
    id: data?.volunteerid || 0,
  });
  const volunteerDetails = VolunteerSelectQuery?.data?.data[0];
  const profileurl = volunteerDetails?.profileimg ? sessionStorage.getItem("filebaseUrl") + "/" + volunteerDetails.profileimg : "";

  useEffect(() => {
    document.title = "View Block Record | ZENTIXS VISTA";
  }, []);

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Block Volunteer", link: BLOCK_VOLUNTEER_PATHS.LIST },
      { text: "View Block Record" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, []);

  if (SelectQuery?.isLoading || SelectQuery?.isFetching || VolunteerSelectQuery.isFetching) {
    return (
      <Container fluid>
        <FullPageLoader message="Fetching details..." />
      </Container>
    );
  }

  if (!data) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ri-error-warning-line display-3 text-warning mb-3 d-block" />
            <h4 className="fw-600">Record Not Found</h4>
            <p className="text-muted">
              The Block Volunteer record you are looking for does not exist or has been removed.
            </p>
          </div>
        </Container>
      </div>
    );
  }

  const handlePreview = (file: any) => {
    const API_BASE_URL =  sessionStorage.getItem("baseUrl") || process.env.REACT_APP_BASE_URL
    const baseUrl = API_BASE_URL?.replace("/api", "") || "";

    const path = file?.filepath || file?.path;
    if (!path) return;

    const finalUrl = path.startsWith("http")
      ? path
      : `${baseUrl}${path.startsWith("/") ? path : "/" + path}`;

    setPreviewFile(null);
    setPreviewFileUrl(finalUrl);
    setPreviewOpen(true);
  };

  const blockTypeStyles: Record<string, { bg: string; text: string; border: string }> = {
    "Temporary": { bg: "bg-warning-subtle", text: "text-warning", border: "border-warning" },
    "Permanent": { bg: "bg-danger-subtle", text: "text-danger", border: "border-danger" },
  };

  const bTypeStyle = blockTypeStyles[data.blocktype] || { bg: "bg-secondary-subtle", text: "text-secondary", border: "border-secondary" };
  const blockTypeBadge = (
    <span
      className={`badge ${bTypeStyle.bg} ${bTypeStyle.text} px-3 py-2 rounded-pill fs-13 border ${bTypeStyle.border}`}
    >
      {data.blocktype || "—"}
    </span>
  );

  return (
    <div className="page-content">
      <Container fluid>
        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center mt-1 mb-3 px-2">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <h5 className="mb-0 fw-bold text-dark">Block Record Details</h5>
            </div>
            <p className="text-muted fs-13 mb-0">
              Subject No: {data.subjectno}&nbsp;|&nbsp;
              Block Type: {data.blocktype}
            </p>
          </div>
        </div>

        <div className="d-flex flex-column gap-0.5">
          {/* SUBJECT IDENTIFICATION */}
          <SectionCard
            icon="ri-user-search-line"
            title="Subject Identification"
            subtitle="Details of the volunteer blocked from study participation."
          >
            <Row>
              <DetailField label="Study Code" value={data.studycode} columns={3} />
              <DetailField label="Subject Number" value={data.subjectno} columns={3} />
              <DetailField label="Subject Initial" value={data.initials} columns={3} />
            </Row>

            {volunteerDetails && (
              <div className={styles.subjectInfoStrip}>
                {profileurl ? (
                  <img src={profileurl} alt={volunteerDetails.initials} className={styles.subjectAvatar} />
                ) : (
                  <div className={styles.subjectAvatar} style={{ background: "#e8f0fe", color: "#3c6bc4", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold" }}>
                    {volunteerDetails.initials}
                  </div>
                )}
                <div className={styles.metaGrid}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Age / Gender</span>
                    <span className={styles.metaValue}>
                      {volunteerDetails.age} / {volunteerDetails.gendertext}
                    </span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Blood Group</span>
                    <span className={styles.metaValue}>{volunteerDetails.bloodgrouptext || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Weight</span>
                    <span className={styles.metaValue}>{volunteerDetails.weight || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Height</span>
                    <span className={styles.metaValue}>{volunteerDetails.height || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>BMI</span>
                    <span className={styles.metaValue}>{volunteerDetails.bmi || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Prior Blocks</span>
                    <span className={styles.metaValue}>{volunteerDetails.priorBlock || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Current Status</span>
                    <span className={`${styles.metaValue} ${styles.statusActive}`}>{volunteerDetails.status}</span>
                  </div>
                </div>
              </div>
            )}
          </SectionCard>

          {/* BLOCK CLASSIFICATION */}
          <SectionCard
            icon="ri-focus-2-line"
            title="Block Classification"
            subtitle="Type, duration, and reason for this block."
          >
            <Row>
              <Col md={3} className="mb-3">
                <label className="text-muted fs-12 mb-1 text-uppercase fw-600 ls-1 d-block">
                  Block Type
                </label>
                <div style={{ minHeight: "24px" }}>{blockTypeBadge}</div>
              </Col>

              <DetailField label="Block Reason" value={data.blockreasontext || data.blockreason} columns={3} />
              <DetailField label="Block Start Date" value={data.startdate ? formatDate(data.startdate) : null} columns={3} />

              {data.blocktype === "Temporary" && (
                <>
                  <DetailField label="Block End Date" value={data.enddate ? formatDate(data.enddate) : null} columns={3} />
                  <DetailField label="Block Duration" value={data.blockduration ? `${data.blockduration}` : "0 Days"} columns={3} />
                  <DetailField label="Re-screening Eligibility" value={data.rescreeningeligibility} columns={3} />
                </>
              )}
            </Row>

            <Row className="mt-2">
              <Col md={12}>
                <label className="text-muted fs-12 mb-2 text-uppercase fw-600 ls-1 d-block">
                  Detailed Remarks
                </label>
                <div
                  className="p-3 bg-light rounded-3 border"
                  style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}
                >
                  {data.remarks || "No detailed remarks provided."}
                </div>
              </Col>
            </Row>
          </SectionCard>

          {/* ATTACHMENTS & VERIFICATION */}
          <SectionCard
            icon="ri-attachment-2"
            title="Attachments & Verification"
            subtitle="Supporting documents and verification details."
          >
            {/* Attachments */}
            {data.attachments && data.attachments.length > 0 ? (
              <div className="mt-1 d-flex flex-column gap-2">
                <p className="text-muted fs-12 text-uppercase fw-medium ls-1 mb-2 d-block">Uploaded Documents</p>

                <Row className="gap-2 px-0 w-100 mx-auto">
                  {data.attachments.map((file: any, idx: number) => (
                    <Col
                      key={idx}
                      md={3}
                      className={`px-0 ${addVolunteerStyle.ipInventory_wrapper}`}
                    >
                      <div className={`${addVolunteerStyle.attachmentCard} h-100`}>
                        {/* FILE INFO */}
                        <div className={addVolunteerStyle.fileInfo}>
                          <div className={addVolunteerStyle.fileIcon}>
                            <i className="ri-file-pdf-line text-danger" />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className={addVolunteerStyle.fileName}>
                              <TruncatedText text={file.filename || file.name || "Attachment"} />
                            </div>
                            <div className={addVolunteerStyle.fileMeta}>
                              <span className="text-uppercase">{file.filetype || "FILE"}</span>
                            </div>
                          </div>
                        </div>

                        <div className={addVolunteerStyle.fileActions}>
                          <i
                            className="ri-eye-line text-primary"
                            title="Preview"
                            style={{ cursor: "pointer" }}
                            onClick={() => handlePreview(file)}
                          />
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            ) : (
              <div className="mt-3">
                <p className="text-muted fs-12 text-uppercase fw-medium ls-1 mb-2 d-block">Uploaded Documents</p>
                <span className="text-muted fst-italic">No attachments uploaded.</span>
              </div>
            )}

            {/* Verified By */}
            {data?.verifiedbytext && (
              <Row className="mt-4">
                <Col md={6}>
                  <label className="form-label text-muted fw-medium fs-12 mb-2 text-uppercase ls-1 d-block mt-2">
                    Verified By
                  </label>
                  <div className="d-flex align-items-center gap-2 p-1">
                    {/* Avatar circle */}
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: 40, height: 40, flexShrink: 0,
                        background: "#e8f0fe", color: "#3c6bc4",
                        fontWeight: 600, fontSize: 13,
                      }}
                    >
                      {data.verifiedbytext
                        .split(" ")
                        .map((n: string) => n[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2)}
                    </div>

                    {/* Name & Role */}
                    <div>
                      <div className="fw-bold text-dark" style={{ fontSize: 14, lineHeight: 1.2 }}>
                        {data.verifiedbytext}
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {data.verifieddate ? formatDate(data.verifieddate) : "—"}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </SectionCard>
        </div>

        {/* File Preview Modal */}
        <FilePreviewModal
          file={previewFile}
          fileUrl={previewFileUrl}
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      </Container>
    </div>
  );
};

export default ViewBlockVolunteer;
